import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { pdApi } from '../api';
import { HelpPanel } from '../HelpPanel';
import { OPPORTUNITIES_DASHBOARD } from '../helpContent';
import './ScoringDashboard.css';

/**
 * SCORING DASHBOARD.
 *
 * Renamed from "Opportunity Dashboard" and rebuilt on the owner's instruction (2026-09-30):
 * *"the current view is very lame. Please look at the executive Dashboard on Project
 * Management module and apply something like that ... nicer graphs"*.
 *
 * Same visual system as that dashboard — graphite, machine blue, one copper accent, hand-drawn
 * SVG marks — but reading Inventory Registry's scoring data, which is the only thing this
 * screen has ever been about: how much of the portfolio has been assessed, how it profiles
 * across the assessment dimensions, and where the scores land against the recommendation
 * bands.
 *
 * The payload is unchanged apart from one addition, `score_bands`, because the old screen
 * could only draw a distribution from the ten rows of `top_scored` — a sample, not a shape.
 */

type Dict = Record<string, unknown>;
const s = (v: unknown) => (v === null || v === undefined ? '' : String(v));
const n = (v: unknown) => Number(v ?? 0);
const pct = (v: number) => Math.round(v) + '%';

/* ── Tooltip ────────────────────────────────────────────────────────────────────────── */

function useTip() {
  const [tip, setTip] = useState<{ html: ReactNode; x: number; y: number } | null>(null);
  const show = useCallback((e: { clientX: number; clientY: number }, html: ReactNode) => {
    setTip({ html, x: e.clientX, y: e.clientY });
  }, []);
  const hide = useCallback(() => setTip(null), []);
  const node = tip ? (
    <div
      className="sdash-tip"
      style={{
        left: Math.min(tip.x + 14, window.innerWidth - 275),
        top: Math.min(tip.y + 14, window.innerHeight - 150),
      }}
    >
      {tip.html}
    </div>
  ) : null;
  return { show, hide, node };
}
const Row = ({ k, v }: { k: string; v: ReactNode }) => (
  <div className="r">
    <span className="k">{k}</span>
    <span>{v}</span>
  </div>
);

const tok = (root: HTMLElement | null, name: string) =>
  root ? getComputedStyle(root).getPropertyValue(name).trim() : '';

/* ── Pieces ─────────────────────────────────────────────────────────────────────────── */

function Panel({ eyebrow, title, note, children }: {
  eyebrow: string; title: string; note?: string; children: ReactNode;
}) {
  return (
    <section className="sd-panel">
      <header>
        <div className="sd-eyebrow">{eyebrow}</div>
        <h2>{title}</h2>
        {note ? <p className="sd-note">{note}</p> : null}
      </header>
      <div className="sd-scroll">{children}</div>
    </section>
  );
}

function Kpi({ label, value, delta, deltaCol, sub, graphic }: {
  label: string; value: string; delta?: string; deltaCol?: string; sub: string; graphic?: ReactNode;
}) {
  return (
    <div className="sd-kpi">
      <div className="sd-eyebrow">{label}</div>
      <div className="v">
        <b>{value}</b>
        {delta ? <span style={{ color: deltaCol }}>{delta}</span> : null}
      </div>
      {graphic ? <div className="sd-spark">{graphic}</div> : null}
      <small>{sub}</small>
    </div>
  );
}

/** Small distribution bars for a KPI tile. Every series here is real, never modelled. */
function MiniBars({ values, colour }: { values: { label: string; v: number }[]; colour: string }) {
  if (!values.some((b) => b.v > 0)) return null;
  const W = 124, H = 26, gap = 3;
  const bw = (W - gap * (values.length - 1)) / values.length;
  const max = Math.max(...values.map((b) => b.v)) || 1;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img" aria-hidden="true">
      {values.map((b, i) => {
        const h = Math.max(b.v > 0 ? 2 : 0, (b.v / max) * (H - 3));
        return (
          <rect key={b.label} x={i * (bw + gap)} y={H - h} width={bw} height={h} rx={1.5}
            fill={colour} fillOpacity={b.v > 0 ? 0.9 : 0.18} />
        );
      })}
    </svg>
  );
}

/**
 * THE PORTFOLIO PROFILE, as a radar.
 *
 * The payload has always called this `portfolioRadar` and the screen drew it as five plain
 * bars — which cannot show the SHAPE of a profile, only five unrelated lengths. A radar puts
 * the dimensions on one closed figure, so a portfolio strong on value and weak on readiness
 * reads as a lopsided shape at a glance.
 */
function Radar({ dims, values, root, show, hide }: {
  dims: string[]; values: number[]; root: HTMLElement | null;
  show: (e: { clientX: number; clientY: number }, h: ReactNode) => void; hide: () => void;
}) {
  const k = Math.min(dims.length, values.length);
  if (k < 3) return <p className="sd-note">Not enough dimensions to draw a profile.</p>;
  const W = 460, H = 330, cx = 168, cy = H / 2, R = 108;
  const ang = (i: number) => (i / k) * 2 * Math.PI - Math.PI / 2;
  const at = (i: number, r: number) => [cx + r * Math.cos(ang(i)), cy + r * Math.sin(ang(i))] as const;
  const poly = (r: (i: number) => number) =>
    Array.from({ length: k }, (_, i) => at(i, r(i)).join(',')).join(' ');
  const accent = tok(root, '--sd-accent');
  const line = tok(root, '--sd-line-soft');
  const muted = tok(root, '--sd-muted');

  return (
    <>
      <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img">
        {[1, 2, 3, 4, 5].map((ring) => (
          <polygon key={ring} points={poly(() => (R * ring) / 5)} fill="none" stroke={line} strokeWidth={1} />
        ))}
        {Array.from({ length: k }, (_, i) => {
          const [x, y] = at(i, R);
          return <line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke={line} strokeWidth={1} />;
        })}
        <polygon points={poly((i) => (R * Math.max(0, Math.min(5, values[i] ?? 0))) / 5)}
          fill={accent} fillOpacity={0.22} stroke={accent} strokeWidth={2} strokeLinejoin="round" />
        {Array.from({ length: k }, (_, i) => {
          const v = Math.max(0, Math.min(5, values[i] ?? 0));
          const [x, y] = at(i, (R * v) / 5);
          return (
            <circle key={i} cx={x} cy={y} r={4} fill={accent} style={{ cursor: 'pointer' }}
              onMouseMove={(e) => show(e, (<><b>{dims[i]}</b><Row k="Average" v={`${v.toFixed(2)} / 5`} /></>))}
              onMouseLeave={hide} />
          );
        })}
        {/* Labels in a column beside the figure — around the rim they collide at five axes. */}
        {Array.from({ length: k }, (_, i) => (
          <g key={i}>
            <text x={320} y={64 + i * 44} style={{ font: "500 11.5px 'IBM Plex Sans',sans-serif", fill: tok(root, '--sd-ink-2') }}>
              {dims[i]}
            </text>
            <text x={320} y={80 + i * 44} style={{ font: "700 13px 'IBM Plex Mono',monospace", fill: accent }}>
              {(values[i] ?? 0).toFixed(2)}
            </text>
            <line x1={308} x2={312} y1={58 + i * 44} y2={84 + i * 44} stroke={accent} strokeWidth={2} />
          </g>
        ))}
        <text x={cx} y={H - 8} textAnchor="middle" style={{ font: "500 10px 'IBM Plex Mono',monospace", fill: muted }}>
          1 – 5 AVERAGE ACROSS SCORED OPPORTUNITIES
        </text>
      </svg>
    </>
  );
}

/** A donut. Used for status, where the whole is meaningful and the parts are few. */
function Donut({ parts, root, show, hide, centreLabel, centreValue }: {
  parts: { label: string; v: number; c: string }[]; root: HTMLElement | null;
  show: (e: { clientX: number; clientY: number }, h: ReactNode) => void; hide: () => void;
  centreLabel: string; centreValue: string;
}) {
  const live = parts.filter((p) => p.v > 0);
  const total = live.reduce((t, p) => t + p.v, 0);
  if (!total) return <div className="sd-empty">Nothing to show yet.</div>;
  const size = 220, cx = size / 2, cy = size / 2, R = 88, T = 26;
  let acc = 0;
  const arc = (from: number, to: number) => {
    const a0 = from * 2 * Math.PI - Math.PI / 2, a1 = to * 2 * Math.PI - Math.PI / 2;
    const large = to - from > 0.5 ? 1 : 0;
    return `M ${cx + R * Math.cos(a0)} ${cy + R * Math.sin(a0)} A ${R} ${R} 0 ${large} 1 ${cx + R * Math.cos(a1)} ${cy + R * Math.sin(a1)}`;
  };
  return (
    <div style={{ display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap' }}>
      <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} role="img">
        {live.map((p) => {
          const from = acc / total, to = (acc + p.v) / total;
          acc += p.v;
          return (
            <path key={p.label} d={arc(from, Math.max(from + 0.0001, to - 0.004))} fill="none"
              stroke={p.c} strokeWidth={T} style={{ cursor: 'pointer' }}
              onMouseMove={(e) => show(e, (
                <><b>{p.label}</b><Row k="Count" v={p.v} /><Row k="Share" v={pct((p.v / total) * 100)} /></>))}
              onMouseLeave={hide} />
          );
        })}
        <text x={cx} y={cy - 2} textAnchor="middle" style={{ font: "700 26px 'IBM Plex Mono',monospace", fill: tok(root, '--sd-ink') }}>
          {centreValue}
        </text>
        <text x={cx} y={cy + 16} textAnchor="middle" style={{ font: "500 10px 'IBM Plex Mono',monospace", fill: tok(root, '--sd-muted') }}>
          {centreLabel}
        </text>
      </svg>
      <div style={{ display: 'grid', gap: 7, minWidth: 150 }}>
        {live.map((p) => (
          <div key={p.label} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
            <i className="sd-sw" style={{ background: p.c }} />
            <span style={{ flex: 1, color: 'var(--sd-ink-2)' }}>{p.label}</span>
            <b style={{ font: "600 12.5px 'IBM Plex Mono',monospace" }}>{p.v}</b>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Horizontal bars with the value on the end — for counts by category. */
function HBars({ rows, root, show, hide, colour }: {
  rows: { label: string; v: number; c?: string }[]; root: HTMLElement | null;
  show: (e: { clientX: number; clientY: number }, h: ReactNode) => void; hide: () => void;
  colour?: string;
}) {
  if (!rows.length) return <div className="sd-empty">Nothing to show yet.</div>;
  const W = 520, rowH = 30, L = 190, R = 52;
  const H = 8 + rows.length * rowH;
  const max = Math.max(1, ...rows.map((r) => r.v));
  const x = (v: number) => L + (v / max) * (W - L - R);
  const total = rows.reduce((t, r) => t + r.v, 0) || 1;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img">
      {rows.map((r, i) => {
        const y = 4 + i * rowH;
        const fill = r.c || colour || tok(root, '--sd-accent');
        return (
          <g key={r.label} style={{ cursor: 'pointer' }}
            onMouseMove={(e) => show(e, (
              <><b>{r.label}</b><Row k="Count" v={r.v} /><Row k="Share" v={pct((r.v / total) * 100)} /></>))}
            onMouseLeave={hide}>
            <text x={0} y={y + 17} style={{ font: "500 11.5px 'IBM Plex Sans',sans-serif", fill: tok(root, '--sd-ink-2') }}>
              {r.label.length > 26 ? r.label.slice(0, 25) + '…' : r.label}
            </text>
            <rect x={L} y={y + 6} width={W - L - R} height={14} rx={3} fill={tok(root, '--sd-line-soft')} />
            <rect x={L} y={y + 6} width={Math.max(2, x(r.v) - L)} height={14} rx={3} fill={fill} />
            <text x={W - R + 8} y={y + 17} style={{ font: "600 12px 'IBM Plex Mono',monospace", fill: tok(root, '--sd-ink') }}>
              {r.v}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/* ── Screen ─────────────────────────────────────────────────────────────────────────── */

export function ScoringDashboard() {
  const [root, setRoot] = useState<HTMLDivElement | null>(null);
  const [data, setData] = useState<Dict | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { show, hide, node: tipNode } = useTip();

  useEffect(() => {
    void (async () => {
      try {
        setData((await pdApi.opportunities.dashboard()) as Dict);
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const statuses = (data?.status_distribution as Dict[]) ?? [];
  const l1 = (data?.l1_distribution as Dict[]) ?? [];
  const recent = (data?.recent_opportunities as Dict[]) ?? [];
  const top = (data?.top_scored_opportunities as Dict[]) ?? [];
  const bands = (data?.score_bands as Dict[]) ?? [];
  const charts = (data?.charts as Dict) ?? {};
  const radar = (charts.portfolioRadar as number[]) ?? [];
  const dims = (charts.dimensions as string[]) ?? [];

  const total = n(data?.total_opportunities);
  const scored = n(data?.scored_opportunities);
  const pending = n(data?.pending_opportunities);
  const avg = n(data?.avg_score);

  /* Status colours: assessed states green through blue, everything else neutral. */
  const statusColour = useMemo(() => {
    const map: Record<string, string> = {
      Scored: '--sd-good', Approved: '--sd-s4', 'Scoring in Progress': '--sd-s3',
      'Ready for Scoring': '--sd-s2', Draft: '--sd-s1',
    };
    return (label: string) => tok(root, map[label] ?? '--sd-line');
  }, [root]);

  const bandRows = bands.map((b) => ({
    label: `${s(b.label)}  (${n(b.min_score).toFixed(2)}–${n(b.max_score).toFixed(2)})`,
    v: n(b.c),
    c: s(b.color_code) || undefined,
  }));

  return (
    <div className="pd-screen sdash" ref={setRoot}>
      <div className="pd-page-header">
        <div>
          <h1 className="pd-page-header__title">Scoring Dashboard</h1>
          <p className="pd-page-header__subtitle">
            How much of the portfolio has been assessed, how it profiles across the assessment
            dimensions, and where the scores land against the recommendation bands.
          </p>
        </div>
        <div className="pd-page-header__actions">
          <HelpPanel help={OPPORTUNITIES_DASHBOARD} />
        </div>
      </div>

      {error ? <div className="pd-error">{error}</div> : null}

      {loading ? (
        <div className="pd-loading">Loading…</div>
      ) : (
        <>
          <div className="sd-kpis">
            <Kpi
              label="Opportunities" value={String(total)} sub="in the registry"
              graphic={<MiniBars values={statuses.map((x) => ({ label: s(x.label), v: n(x.c) }))}
                colour={tok(root, '--sd-accent')} />}
            />
            <Kpi
              label="Assessed" value={String(scored)}
              delta={total ? pct((scored / total) * 100) : '—'} deltaCol={tok(root, '--sd-good')}
              sub="Scored or Approved"
              graphic={<MiniBars values={bandRows.map((b) => ({ label: b.label, v: b.v }))}
                colour={tok(root, '--sd-good')} />}
            />
            <Kpi
              label="Outstanding" value={String(pending)}
              delta={pending ? 'to assess' : 'none'}
              deltaCol={pending ? tok(root, '--sd-warn') : tok(root, '--sd-good')}
              sub="not yet Scored or Approved"
            />
            <Kpi
              label="Average score" value={avg ? avg.toFixed(2) : '—'}
              delta={avg ? 'of 5.00' : undefined} deltaCol={tok(root, '--sd-muted')}
              sub="across every scored opportunity"
              graphic={<MiniBars values={dims.map((d, i) => ({ label: d, v: radar[i] ?? 0 }))}
                colour={tok(root, '--sd-accent')} />}
            />
          </div>

          <div className="sd-grid sd-g2">
            <Panel eyebrow="Profile" title="How the portfolio scores by dimension"
              note="The average of every scored opportunity on each assessment dimension. A lopsided figure is a portfolio strong in one respect and weak in another.">
              {radar.length ? (
                <Radar dims={dims} values={radar} root={root} show={show} hide={hide} />
              ) : (
                <div className="sd-empty">No opportunity has been scored yet.</div>
              )}
            </Panel>

            <Panel eyebrow="Coverage" title="Where the portfolio stands"
              note="Every opportunity by status. The centre is the share that has been assessed.">
              <Donut
                parts={statuses.map((x) => ({ label: s(x.label), v: n(x.c), c: statusColour(s(x.label)) }))}
                root={root} show={show} hide={hide}
                centreLabel="ASSESSED" centreValue={total ? pct((scored / total) * 100) : '—'}
              />
            </Panel>
          </div>

          <div className="sd-grid sd-g2">
            <Panel eyebrow="Distribution" title="Scores against the recommendation bands"
              note="Every scored opportunity counted into its band, in the colours the recommendation matrix defines. An empty band is shown rather than dropped, so the scale stays honest.">
              {bandRows.length ? (
                <HBars rows={bandRows} root={root} show={show} hide={hide} />
              ) : (
                <div className="sd-empty">No recommendation bands are configured.</div>
              )}
            </Panel>

            <Panel eyebrow="Coverage" title="Opportunities by L1 process"
              note="Where the portfolio's demand actually sits.">
              {l1.length ? (
                <HBars rows={l1.map((x) => ({ label: s(x.label), v: n(x.c) }))}
                  root={root} show={show} hide={hide} colour={tok(root, '--sd-s3')} />
              ) : (
                <div className="sd-empty">No opportunities are filed under an L1 process.</div>
              )}
            </Panel>
          </div>

          <div className="sd-grid">
            <Panel eyebrow="Ranking" title="Top scored opportunities"
              note="The ten highest scores, with the recommendation each one earned.">
              {top.length ? (
                <div className="sd-rank">
                  {top.map((o, i) => {
                    const v = n(o.overall_score);
                    const colour = s(o.recommendation_color) || tok(root, '--sd-accent');
                    return (
                      <div className="sd-rank-row" key={s(o.opportunity_id)}>
                        <span className="sd-rank-n">{i + 1}</span>
                        <span style={{ minWidth: 0 }}>
                          <span className="sd-rank-t">
                            <span className="sd-id">{s(o.opportunity_id)}</span>{' '}
                            {s(o.description)}
                          </span>
                          <span className="sd-rank-bar">
                            <i style={{ width: `${Math.max(2, (v / 5) * 100)}%`, background: colour }} />
                          </span>
                        </span>
                        <span style={{ whiteSpace: 'nowrap' }}>
                          <span className="sd-rank-v">{v.toFixed(2)}</span>
                          {s(o.recommendation) ? (
                            <span className="sd-chip" style={{ background: `${colour}22`, color: colour }}>
                              {s(o.recommendation)}
                            </span>
                          ) : null}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="sd-empty">No opportunity has been scored yet.</div>
              )}
            </Panel>

            <Panel eyebrow="Activity" title="Recently added">
              {recent.length ? (
                <table>
                  <thead>
                    <tr>
                      <th>Opportunity</th><th>Project</th><th>Status</th>
                      <th style={{ textAlign: 'right' }}>Score</th>
                      <th style={{ textAlign: 'right' }}>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recent.map((o) => (
                      <tr key={s(o.opportunity_id)}>
                        <td>
                          <span className="sd-id">{s(o.opportunity_id)}</span>
                          <span style={{ display: 'block', lineHeight: 1.35 }}>{s(o.description)}</span>
                        </td>
                        <td>{s(o.project_description) || '—'}</td>
                        <td>{s(o.status)}</td>
                        <td className="num">
                          {o.overall_score === null || o.overall_score === undefined
                            ? '—' : n(o.overall_score).toFixed(2)}
                        </td>
                        <td className="num">{s(o.created_date).slice(0, 10) || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="sd-empty">No opportunities yet.</div>
              )}
            </Panel>
          </div>
        </>
      )}
      {tipNode}
    </div>
  );
}
